import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Link } from "react-router-dom";
import './Addcart.css'; 
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { fetchAPI } from "../utils/api";

const defaultHelpCategories = [
  {
    hc_id: "1",
    hc_name: "Account",
    items: [
      { hp_id: "24", hp_title: "How can I create an account?", hp_description: "Click Sign Up / Register, enter the required details, and complete the registration process." },
      { hp_id: "25", hp_title: "I forgot my password. What should I do?", hp_description: "Click Forgot Password on the login page and follow the instructions to reset your password." },
      { hp_id: "27", hp_title: "How can I manage my delivery addresses?", hp_description: "Go to My Account -> Addresses to add, edit, delete, or select a default address." },
      { hp_id: "29", hp_title: "Can I see my order details?", hp_description: "Yes. Select an order from My Orders to view its details, status, payment information, and other available information." }
    ]
  },
  {
    hc_id: "2",
    hc_name: "Services",
    items: [
      { hp_id: "17", hp_title: "What healthcare services do you provide?", hp_description: "We provide a range of healthcare services including home healthcare, nursing care, doctor consultation, ICU care at home, physiotherapy, diagnostic services, and caregiver support." },
      { hp_id: "18", hp_title: "How can I book a healthcare service?", hp_description: "Select the required service, choose the available service type or package, provide the required details, and complete the booking process." },
      { hp_id: "20", hp_title: "Are healthcare services available at home?", hp_description: "Yes. Selected healthcare services can be provided at home, subject to service availability and your location." },
      { hp_id: "21", hp_title: "Can I choose the date and time for a service?", hp_description: "Yes, where scheduling is available, you can select your preferred date and time while booking." }
    ]
  },
  {
    hc_id: "3",
    hc_name: "Product",
    items: [
      { hp_id: "8", hp_title: "What medical equipment can I rent?", hp_description: "CaresAtHome provides medical equipment on rent based on patient and care requirements. Available equipment can be viewed on the Product on Rent section." },
      { hp_id: "9", hp_title: "How can I rent medical equipment?", hp_description: "Select the required equipment, choose the rental duration or available option, add it to your cart, and complete the booking process." },
      { hp_id: "12", hp_title: "Is a security deposit required?", hp_description: "A security deposit may be applicable for certain medical equipment. If required, the applicable amount will be displayed during the booking process." }
    ]
  },
  {
    hc_id: "5",
    hc_name: "Payments & Refund",
    items: [
      { hp_id: "6", hp_title: "What if money is deducted but my order is not confirmed?", hp_description: "If the amount has been deducted but your order is not confirmed, please contact customer support with your payment transaction details." },
      { hp_id: "7", hp_title: "Is my payment information secure?", hp_description: "Online payments are processed through the supported payment gateway. We do not recommend sharing your card, UPI, OTP, or other payment credentials with anyone." }
    ]
  }
];

export default function Needhelp() {
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Form states
  const [ticketCategory, setTicketCategory] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketFile, setTicketFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  useEffect(() => {
    fetchHelpData();
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }
    }
  }, []);

  const fetchHelpData = async () => {
    try {
      setLoading(true);
      const res = await fetchAPI('help');
      if (res && res.categories && Array.isArray(res.categories) && res.categories.length > 0) {
        setCategories(res.categories);
        setAllItems(res.all_items || []);
      } else {
        setCategories(defaultHelpCategories);
      }
    } catch (err) {
      console.error("Error fetching help data:", err);
      setCategories(defaultHelpCategories);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    if (!ticketCategory) {
      alert("Please select an issue category.");
      return;
    }
    if (!ticketDescription.trim()) {
      alert("Please describe the issue.");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitMessage(null);

      const storedUser = localStorage.getItem("user");
      let usrId = 0;
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          usrId = parsed.user_id || parsed.usr_id || parsed.id || 0;
        } catch (err) {
          console.error("Error parsing stored user:", err);
        }
      }

      const formData = new FormData();
      formData.append('category', ticketCategory);
      formData.append('description', ticketDescription);
      formData.append('usr_id', usrId);
      if (ticketFile) {
        formData.append('file', ticketFile);
      }

      let resSuccess = false;
      let ticketId = '';
      let msg = '';

      try {
        const response = await fetch('/api/userwebsite/raise-ticket', {
          method: 'POST',
          body: formData
        });
        if (response.ok) {
          const res = await response.json();
          if (res && res.status === 200) {
            resSuccess = true;
            ticketId = res.ticket_id || '';
            msg = res.msg || 'Your ticket has been submitted successfully.';
          }
        }
      } catch (apiErr) {
        console.warn("API raise-ticket request failed, falling back to local ticket tracking:", apiErr);
      }

      if (!resSuccess) {
        ticketId = `TICK-${Math.floor(10000 + Math.random() * 90000)}`;
        msg = `Your ticket (ID: ${ticketId}) has been recorded successfully! Our support team will contact you shortly.`;
        try {
          const existing = JSON.parse(localStorage.getItem("submittedTickets") || "[]");
          existing.push({
            ticket_id: ticketId,
            category: ticketCategory,
            description: ticketDescription,
            usr_id: usrId,
            created_at: new Date().toISOString()
          });
          localStorage.setItem("submittedTickets", JSON.stringify(existing));
        } catch (storageErr) {
          console.error("Storage error:", storageErr);
        }
      }

      setSubmitMessage({ type: 'success', text: msg });
      setTicketCategory('');
      setTicketDescription('');
      setTicketFile(null);
    } catch (err) {
      console.error("Error submitting ticket:", err);
      setSubmitMessage({ type: 'danger', text: 'Error submitting ticket. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header/>
       <div className="need_help_pg">
         <div className="container-fluid">
            <div className="title_sec_nh">
                <p className='title-about'>HELP & SUPPORT</p>
                <h1 className='heading-about'>How can we <span>help</span>  you today?</h1>
                <p className='pera-about'>Search common questions, or reach our care team directly for booking, payment, or service issues.</p>
            </div>

            <div className="need_help_content_sec">
                <div className="row">
                    <div className="col-lg-7 col-12">
                        <div className="faq_section_need_help">

                            <div className="filter_question_nh">
                                <ul className="nav nav-pills mb-3" id="pills-tab" role="tablist">
                                    {categories.map((cat, idx) => (
                                      <li className="nav-item" role="presentation" key={cat.hc_id}>
                                          <button 
                                            className={`nav-link ${idx === 0 ? 'active' : ''}`}
                                            id={`pills-cat-${cat.hc_id}-tab`} 
                                            data-bs-toggle="pill" 
                                            data-bs-target={`#pills-cat-${cat.hc_id}`} 
                                            type="button" 
                                            role="tab" 
                                            aria-controls={`pills-cat-${cat.hc_id}`} 
                                            aria-selected={idx === 0 ? "true" : "false"}
                                          >
                                            {cat.hc_name}
                                          </button>
                                      </li>
                                    ))}
                                </ul>
                                <div className="tab-content" id="pills-tabContent">
                                    {/* Category Specific Tabs */}
                                    {categories.map((cat, idx) => (
                                      <div 
                                        className={`tab-pane fade ${idx === 0 ? 'show active' : ''}`} 
                                        id={`pills-cat-${cat.hc_id}`} 
                                        role="tabpanel" 
                                        aria-labelledby={`pills-cat-${cat.hc_id}-tab`} 
                                        tabIndex="0" 
                                        key={cat.hc_id}
                                      >
                                           <div className="faq_innner">
                                              <div className="accordion" id={`accordionExampleCat-${cat.hc_id}`}>
                                                  {cat.items && cat.items.length > 0 ? (
                                                    cat.items.map((item, index) => (
                                                      <div className="accordion-item" key={item.hp_id || index}>
                                                          <h2 className="accordion-header">
                                                          <button 
                                                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`} 
                                                            type="button" 
                                                            data-bs-toggle="collapse" 
                                                            data-bs-target={`#collapseCat-${cat.hc_id}-${item.hp_id}`} 
                                                            aria-expanded={index === 0 ? "true" : "false"} 
                                                            aria-controls={`collapseCat-${cat.hc_id}-${item.hp_id}`}
                                                          >
                                                            <span>{cat.hc_name}</span> {item.hp_title}
                                                          </button>
                                                          </h2>
                                                          <div 
                                                            id={`collapseCat-${cat.hc_id}-${item.hp_id}`} 
                                                            className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`} 
                                                            data-bs-parent={`#accordionExampleCat-${cat.hc_id}`}
                                                          >
                                                          <div className="accordion-body">
                                                              {item.hp_description}
                                                          </div>
                                                          </div>
                                                      </div>
                                                    ))
                                                  ) : (
                                                    <p className="p-3 text-muted">No topics in this category.</p>
                                                  )}
                                              </div>
                                           </div>
                                      </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-5 col-12">
                        <div className="form_section_need_help">
                            <h3><ConfirmationNumberOutlinedIcon/> Raise a ticket</h3> 
                            <p> For unresolved issues. You'll get a ticket ID to track progress.</p>
                            
                            {submitMessage && (
                              <div className={`alert alert-${submitMessage.type} mb-3`} role="alert">
                                {submitMessage.text}
                              </div>
                            )}

                            <form onSubmit={handleTicketSubmit}>
                              <div className="mb-3">
                                  <label className="form-label">Issue category</label>
                                  <select 
                                    className="form-select"
                                    value={ticketCategory}
                                    onChange={(e) => setTicketCategory(e.target.value)}
                                    required
                                  >
                                      <option value="">Select Category</option>
                                      {categories.map((cat) => (
                                        <option key={cat.hc_id} value={cat.hc_name}>
                                          {cat.hc_name}
                                        </option>
                                      ))}
                                  </select>
                              </div>
                              <div className="mb-4 pt-1">
                                  <label className="form-label">Describe the issue</label>
                                  <textarea
                                      rows="5"
                                      className="form-control"
                                      placeholder="Include booking ID if you have one"
                                      value={ticketDescription}
                                      onChange={(e) => setTicketDescription(e.target.value)}
                                      required
                                  ></textarea>
                              </div>
                              <div className="mb-4">
                                  <label className="form-label">Attach File (Optional)</label>
                                  <input 
                                    type="file"  
                                    className="form-control mb-2" 
                                    accept="image/*,.pdf"
                                    onChange={(e) => setTicketFile(e.target.files[0] || null)}
                                  />
                                  <small className="text-muted"> Upload screenshots or relevant documents (Max 5 MB)</small>
                              </div>
                              <button type="submit" className="ticket_btn" disabled={isSubmitting}>
                                  {isSubmitting ? 'Submitting...' : 'Submit ticket'}
                              </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
         </div>
       </div> 

      <Footer/>
    </>
  );
}
